/**
 * loadReport.js — замер последней загрузки и правило «показывать ли его на экране».
 *
 * ПОЧЕМУ ПРАВИЛО, А НЕ ПРОСТО «УБРАТЬ». Окно с замером появилось не от скуки:
 * замеры делаются на телефоне, а консоли на Android нет — печатать время в
 * консоль разработчика значит мерить на телефоне и смотреть на компьютере. В
 * ЭТАЛОНЕ это окно так и записано: «на экране, не в консоли». Убрать его совсем
 * — значит снова ослепнуть на телефоне.
 *
 * Но врач прав: после каждой нормальной загрузки читать «42.6 с на 1069 файлов
 * (39.9 мс на файл). Фоновое сохранение: вкл» и закрывать плашку — работа не
 * его. Технический замер интересен ровно в двух случаях:
 *
 *   1. НЕ СОШЛОСЬ: файлов принято больше, чем получилось срезов. Это единственная
 *      беда, которую по картинке не увидишь: программа покажет исследование,
 *      просто часть срезов в нём не будет. Ради этой строки всё и затевалось.
 *   2. ДОЛГО НА ФАЙЛ: признак возврата той самой беды, из-за которой загрузка
 *      каталога занимала часы (3400 мс на файл до 10.5.8 против 31 мс после).
 *      Порог 150 мс — в четыре раза выше нынешних сорока и в двадцать раз ниже
 *      прежней беды: нормальная загрузка молчит, возврат регресса — говорит.
 *
 * Во всех остальных случаях замер идёт в журнал и остаётся здесь — его можно
 * посмотреть кнопкой в Enterprise → Диагностика, когда он действительно нужен.
 *
 * Файл без импортов: правило проверяется тестом, а не запуском программы.
 */

/** Выше этого — загрузка подозрительно медленная и об этом стоит сказать. */
export const ПОРОГ_МС_НА_ФАЙЛ = 150;

// ══════════════════════════════════════════════════════════════
//  [11.1.35] ВЫКЛЮЧАТЕЛЬ ПЛАШЕК ЗАМЕРА — по слову владельца:
//  «панель была нужна для диагностики, а сейчас я её хочу скрыть.
//  Скрыть, а не удалить». Скрываются ОБЕ плашки о скорости (замер обхода
//  каталога и замер времени загрузки), и их WARN-строки в журнале
//  опускаются до info. Механизм жив: кнопка «Замер последней загрузки»
//  в Диагностике показывает его по требованию в любой момент.
//
//  ЧЕСТНАЯ ОГОВОРКА, одна: сверка «файлов принято N — срезов M» при
//  ПОТЕРЕ срезов показывается и при выключенной плашке. Это не замер
//  скорости, а предупреждение о неполных данных: исследование на экране
//  выглядит целым, а срезов в нём меньше — по картинке этого не увидеть,
//  а меряться будет уже неправда. Правило это — здесь, чистой функцией,
//  чтобы проверялось без браузера.
// ══════════════════════════════════════════════════════════════

export const КЛЮЧ_ПЛАШКИ_ЗАМЕРА = 'mikhmel_load_meter';

/** Выключена ли плашка замера. Принимает СЫРОЕ значение из localStorage. */
export function плашкаЗамераВыключена(значение) {
  return String(значение || '') === 'off';
}

/**
 * Итоговое решение о показе с учётом выключателя.
 * @param {{показать:boolean, причина:string}} реши — вердикт нужноПоказать
 * @param {{плохо:boolean}|null} сверка
 * @param {boolean} выключена — плашкаЗамераВыключена(localStorage…)
 */
export function показыватьСУчётомВыключателя(реши, сверка, выключена) {
  if (!выключена) return реши;
  if (сверка && сверка.плохо) {
    return { показать: true, причина: 'потеря срезов показывается и при выключенной плашке' };
  }
  return { показать: false, причина: 'плашка скрыта настройкой (Диагностика) — замер в журнале и по кнопке' };
}

let _последний = null;

/**
 * Запомнить замер. Зовётся в конце каждой загрузки — всегда, независимо от
 * того, покажем ли мы его на экране.
 *
 * @param {{текст: string, сверка: {текст: string, плохо: boolean}|null,
 *          файлов: number, мсНаФайл: number, секунд: number}} з
 */
export function запомнитьЗамер(з) {
  _последний = {
    текст: String(з?.текст || ''),
    сверка: з?.сверка ? { текст: String(з.сверка.текст || ''), плохо: !!з.сверка.плохо } : null,
    файлов: Number(з?.файлов) || 0,
    мсНаФайл: Number(з?.мсНаФайл) || 0,
    секунд: Number(з?.секунд) || 0,
    при: Number(з?.при) || 0,
    // [10.19.23] Строки этапов (обход / чтение / разбор / регистрация) хранятся
    // вместе с замером: именно они называют виновника замедления, а одно число
    // «мс на файл» только сообщает, что он есть.
    этапы: Array.isArray(з?.этапы) ? з.этапы.map(String) : null,
  };
  return _последний;
}

/** Последний замер — для кнопки в «Диагностике» и для проверок. */
export function последнийЗамер() { return _последний ? { ..._последний } : null; }

/** Забыть (новый сеанс, очистка). */
export function забытьЗамер() { _последний = null; }

/**
 * Показывать ли плашку на экране.
 *
 * @param {{сверка?: {плохо: boolean}|null, мсНаФайл?: number, порог?: number}} з
 * @returns {{показать: boolean, причина: string}}
 */
export function нужноПоказать(з = {}) {
  const порог = Number.isFinite(з.порог) ? з.порог : ПОРОГ_МС_НА_ФАЙЛ;
  if (з.сверка?.плохо) {
    return { показать: true, причина: 'не сошлось: часть файлов не стала срезами' };
  }
  const мс = Number(з.мсНаФайл);
  if (Number.isFinite(мс) && мс > порог) {
    return { показать: true, причина: `долго на файл: ${мс.toFixed(1)} мс при пороге ${порог}` };
  }
  // [10.19.23] Плашка нужна и тогда, когда замер САМ нашёл патологию: повторные
  // чтения, таймауты, единственный рабочий поток. Такое бывает при нормальном
  // среднем времени — например, когда медленный этап один, а остальные быстры, —
  // и молчать об этом значит снова оставить врача без объяснения.
  if (Array.isArray(з.этапы) && з.этапы.length > 2) {
    return { показать: true, причина: 'замер нашёл патологию этапа — она названа в плашке' };
  }
  return { показать: false, причина: 'всё сошлось и уложилось в срок — замер только в журнале' };
}

export default {
  ПОРОГ_МС_НА_ФАЙЛ, запомнитьЗамер, последнийЗамер, забытьЗамер, нужноПоказать,
};
